import { FormEvent, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  ButtonGroup,
  Input,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Selection,
  Textarea,
  useDisclosure,
} from '@nextui-org/react';
import { FaAngleRight, FaPlus } from 'react-icons/fa6';
import Icon from '../base/Icon';
import ChatListItem from './ChatListItem';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectCurrentClient,
  selectCurrentClientId,
  setCurrentClient,
} from '../../store/client/clientSlice';
import {
  clearMessagesWithClient,
  selectChats,
} from '../../store/chat/chatSlice';
import MarkdownWrapper from '../base/MarkdownWrapper';
import { selectUserName, selectUserRole } from '../../store/auth/authSlice';
import { selectCustomActors } from '../../store/actor/actorSlice';
import {
  getCustomActors,
  saveCustomActor,
} from '../../store/actor/actorThunks';

const HELP_TEXT = `You can use the following special tokens \
to inject preset instructions into your prompt:

- \`{actor_instructions}\`: generic task instructions for the AI. \
- \`{style_informal}\`: instructions to encourage imperfect English \
in a conversational style; also enables multiple-message functionality.
- \`{guidelines}\`: specific rules to increase task performance.

Example prompt:

\`\`\`text
{actor_instructions}

Scenario:
You are a 20 year old experiencing_

Emotional Struggles:
Extreme sadness, isolation, suicidal ideation etc.

Chatting Style:
{style_informal}
Generate short responses and use occasional short-forms. 

Guidelines:
{guidelines}
\`\`\`
`;

export default function ChatListCustomActors() {
  const dispatch = useAppDispatch();
  const actors = useAppSelector(selectCustomActors);
  const chats = useAppSelector(selectChats);
  const currentClientId = useAppSelector(selectCurrentClientId);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const currentClient = useAppSelector(selectCurrentClient);
  const owner = useAppSelector(selectUserName);
  const userRole = useAppSelector(selectUserRole);

  useEffect(() => {
    dispatch(getCustomActors(owner));
  }, [dispatch, owner]);

  const actorsArray = useMemo(
    () => Array.from(Object.values(actors)),
    [actors]
  );

  const actorIds = useMemo(
    () => actorsArray.map((c) => c.userId),
    [actorsArray]
  );

  useEffect(() => {
    if (!currentClientId || !actorIds.includes(currentClientId)) {
      // current client is not an actor
      return;
    }
    if (currentClientId in chats) {
      // current client has a chat
      return;
    }

    dispatch(clearMessagesWithClient(currentClientId));
  }, [dispatch, actorIds, chats, currentClientId]);

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    for (const k of keys.values()) {
      const userId = k.toString();
      dispatch(setCurrentClient(userId));
    }
  };

  const handleCustomActorCreate =
    (onClose: () => void) => (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const form = new FormData(e.currentTarget);
      const username = form.get('username')?.toString();
      const prompt = form.get('prompt')?.toString();
      const isCustomActor = true;

      dispatch(
        saveCustomActor({
          userId: uuidv4(),
          userType: 'actor',
          username,
          prompt,
          isCustomActor,
          owner: owner,
        })
      );

      onClose();
    };

  return (
    <div className="relative flex flex-col mb-2">
      <Listbox
        items={actorsArray}
        itemClasses={{ base: 'rounded-medium' }}
        variant="flat"
        selectionMode="single"
        selectedKeys={currentClient?.userId ? [currentClient.userId] : []}
        onSelectionChange={handleSelectionChange}
        emptyContent={<p>No actors.</p>}
        aria-label="Actors chat list"
      >
        {(c) => (
          <ListboxItem
            key={c.userId}
            textValue={c.userId}
            selectedIcon={(props) =>
              props.isSelected && (
                <Icon icon={FaAngleRight} className="w-3 h-3" />
              )
            }
            className={`${currentClient ? (c.userId === currentClient.userId ? 'highlight-list-item' : '') : ''}`}
          >
            <ChatListItem
              showAvatar
              client={c}
              messages={{
                actor_description: {
                  messageId: 'actor_description',
                  role: 'assistant',
                  content: c.userId
                    .split('_')
                    .map((s) => s.trim())
                    .filter((s) => !!s)
                    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                    .join(' '),
                  createdAt: Date.now().toString(),
                },
              }}
            />
          </ListboxItem>
        )}
      </Listbox>

      {userRole !== 'user_trainee' && (
        <Button
          color="default"
          variant="flat"
          startContent={<Icon icon={FaPlus} />}
          onPress={onOpen}
        >
          Custom actor
        </Button>
      )}

      <Modal size="xl" isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleCustomActorCreate(onClose)}>
              <ModalHeader>Create custom actor</ModalHeader>
              <ModalBody>
                <Input
                  autoFocus
                  isRequired
                  name="username"
                  placeholder="Friendly name"
                />
                <Textarea
                  isRequired
                  name="prompt"
                  placeholder="Custom prompt"
                />
                <MarkdownWrapper>{HELP_TEXT}</MarkdownWrapper>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup variant="solid">
                  <Button onPress={onClose}>Cancel</Button>
                  <Button type="submit" color="primary">
                    Create
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
